import './pages.css';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../Config';

const TIERS = [
  {
    id: 'shared',
    name: 'Shared',
    price: 0,
    priceSub: 'usage only / month',
    desc: 'Get started instantly on shared infrastructure. No cluster fee — pay only for what you run.',
    accentClass: 'pv2-accent-slate',
    features: [
      'Shared Mermadic cluster',
      'Job runs & image pushes',
      'Best-effort uptime',
      'No setup required',
    ],
    hasDeployment: false,
    cta: 'Get Started Free',
    ctaAlt: false,
    featured: false,
  },
  {
    id: 'dev',
    name: 'Gold',
    priceSub: '+ usage / month',
    desc: 'Your own dedicated multi-AZ cluster for development and staging workloads.',
    accentClass: 'pv2-accent-indigo',
    features: [
      'Dedicated cluster (multi-AZ)',
      '2 always-on medium nodes, scales to 5',
      'Up to 10 job-only nodes (2 vCPU / 8 GB each, scale to zero when idle)',
      'Monitoring dashboard',
      'Self-managed disaster recovery',
      'Deployment backups',
      'Email support (48 hr SLA)',
      '99 % uptime target',
    ],
    hasDeployment: true,
    cta: 'Get Started',
    ctaAlt: false,
    featured: false,
  },
  {
    id: 'prod',
    name: 'Premium',
    priceSub: '+ usage / month',
    desc: 'Your own dedicated multi-AZ cluster built for production traffic and higher availability.',
    accentClass: 'pv2-accent-blue',
    features: [
      'Dedicated cluster (multi-AZ)',
      '2 always-on m5.xlarge nodes, scales to 6',
      '0–2 burst m5.2xlarge nodes for heavy workloads',
      'Up to 20 job-only nodes (4 vCPU / 16 GB each, scale to zero when idle)',
      'Optional GPU jobs (1× NVIDIA T4, usage-billed, scale to zero when idle)',
      'Monitoring dashboard',
      'Self-managed disaster recovery',
      'Deployment backups',
      '99.5 % uptime SLA',
    ],
    hasDeployment: true,
    cta: 'Get Started',
    ctaAlt: false,
    featured: true,
    badge: 'Most Popular',
  },
];

const COMPARE_ROWS = [
  { label: 'Setup time',                            mermadic: '~1 hour',    diy: '2–6 months',   batch: '1–2 weeks',    byoc: '1–3 weeks' },
  { label: 'Cloud account required',                mermadic: 'No',         diy: 'Yes',          batch: 'Yes',          byoc: 'Yes (your own)' },
  { label: 'Monthly bills',                         mermadic: '1',          diy: '1+ (cloud)',   batch: '1 (AWS)',       byoc: '2 (platform + cloud)' },
  { label: 'Est. all-in: Gold, light–moderate',       mermadic: '~$375–500', diy: '$10,000+',    batch: '$500–2,000',  byoc: '~$236*' },
  { label: 'Est. all-in: 5 deploys + 5K jobs/mo',  mermadic: '~$750–900',  diy: '$15,000+',     batch: '$3,000–6,000', byoc: '~$435 platform + ~$690 cloud = ~$1,125*' },
  { label: 'Infrastructure maintenance',            mermadic: 'Zero',       diy: '20+ hr/week',  batch: '5–10 hr/week', byoc: '3–8 hr/week' },
  { label: 'Long-running deployments',              mermadic: '✓',          diy: '✓',            batch: '✗',            byoc: '✓' },
  { label: 'Auto-scaling',                          mermadic: '✓',          diy: 'Manual',       batch: '✓ (costly)',   byoc: '✓ (adds to cloud bill)' },
  { label: 'Dedicated cluster',                     mermadic: '✓',          diy: '✓',            batch: '✗',            byoc: '✓' },
  { label: 'Self-managed disaster recovery',        mermadic: '✓',          diy: 'Manual',       batch: '✗',            byoc: 'Limited' },
];


const COST_SCENARIOS = [
  {
    label: 'Light usage',
    desc: '2 always-on deployments · 500 jobs/month',
    note: 'BYOC may look cheaper here — but it excludes setup time, cloud IAM configuration, and ongoing cluster maintenance.',
    mermadic: { plan: 'Gold', base: '$375', usage: '~$45', total: '~$420', single_bill: true },
    byoc: {
      platform: '~$53 (platform markup)',
      cloud: '~$183 (EKS + EC2 + networking)',
      total: '~$236',
      note: 'Excludes cloud support plan, DevOps setup time (10–40 hrs), and ongoing cluster maintenance.',
      single_bill: false,
    },
  },
  {
    label: 'Heavy usage',
    desc: '5 always-on deployments · 5,000 jobs/month',
    note: 'At scale, the BYOC platform markup and your cloud bill compound together — both grow as you add workloads.',
    mermadic: { plan: 'Premium', base: '$500', usage: '~$280', total: '~$780', single_bill: true },
    byoc: {
      platform: '~$435 (platform markup)',
      cloud: '~$690 (EKS + EC2 + networking)',
      total: '~$1,125',
      note: 'BYOC platforms charge a per-resource platform fee on top of your full cloud provider bill — both bills scale together as you grow.',
      single_bill: false,
    },
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    fetch(`${config.MASTER_API}/v1/billing/pricing`)
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setPricing(data); })
      .catch(() => {});
  }, []);

  const tierPrice = (tier) => {
    if (tier.id === 'shared') return tier.price;
    const val = pricing?.pricing?.[tier.id];
    return val != null ? val : null;
  };

  const fmt = (n) => (n != null ? `$${n}` : '…');

  const tierUsageRows = (tier) => {
    const job = pricing?.jobUsagePrice?.[tier.id];
    const deplCpuHour = pricing?.deploymentUsagePrice?.per_cpu_hour;
    const deplGbHour  = pricing?.deploymentUsagePrice?.per_gb_hour;
    if (!job) return null;
    const rows = [
      { label: 'Per job execution',          value: fmt(job.per_job_cost) },
      { label: 'Per vCPU-hour (job)',         value: fmt(job.cpu_hour) },
      { label: 'Per GB-hour memory (job)',    value: fmt(job.gb_hour) },
    ];
    // GPU is opt-in and priced per tier, so the row only appears where it is offered.
    // The rate replaces the vCPU and memory rates above rather than adding to them:
    // a GPU job has a whole node to itself, so its CPU and memory are included.
    if (job.gpu_hour) {
      const minMinutes = job.gpu_min_billable_minutes;
      rows.push({
        label: minMinutes
          ? `Per GPU-hour (job, all-in, ${minMinutes}-min minimum)`
          : 'Per GPU-hour (job, all-in)',
        value: fmt(job.gpu_hour),
      });
    }
    if (tier.hasDeployment) {
      rows.push({ label: 'Per vCPU-hour (deployment)',      value: deplCpuHour != null ? fmt(deplCpuHour) : '…' });
      rows.push({ label: 'Per GB-hour memory (deployment)', value: deplGbHour  != null ? fmt(deplGbHour)  : '…' });
    }
    return rows;
  };

  const devJob      = pricing?.jobUsagePrice?.dev;
  const prodJob     = pricing?.jobUsagePrice?.prod;
  const deplCpuHour = pricing?.deploymentUsagePrice?.per_cpu_hour;
  const deplGbHour  = pricing?.deploymentUsagePrice?.per_gb_hour;

  return (
    <div className="pv2-page">

      {/* ── Hero ── */}
      <section className="pv2-hero">
        <div className="pv2-hero-inner">
          <span className="pv2-eyebrow">Pricing</span>
          <h1 className="pv2-headline">Simple, transparent pricing</h1>
          <p className="pv2-subline">
            Pay only for what you use. A flat base fee covers your dedicated cluster —
            usage charges scale with your actual workload.
            No AWS or cloud provider account required — infrastructure is fully managed for you.
          </p>
          <div className="pv2-formula-pill">
            <span className="pv2-formula-label">Monthly Bill</span>
            <span className="pv2-formula-eq">=</span>
            <span>Base Fee</span>
            <span className="pv2-formula-op">+</span>
            <span>{devJob ? `$${devJob.per_job_cost}` : '…'} × Jobs</span>
            <span className="pv2-formula-op">+</span>
            <span>{devJob ? `$${devJob.cpu_hour}` : '…'} × vCPU-hr (jobs)</span>
            <span className="pv2-formula-op">+</span>
            <span>{devJob ? `$${devJob.gb_hour}` : '…'} × GB-hr (jobs)</span>
            <span className="pv2-formula-op">+</span>
            <span>{deplCpuHour != null ? `$${deplCpuHour}` : '…'} × vCPU-hr (deploys)</span>
            <span className="pv2-formula-op">+</span>
            <span>{deplGbHour != null ? `$${deplGbHour}` : '…'} × GB-hr (deploys)</span>
          </div>
          {prodJob?.gpu_hour && (
            <p className="pv2-subline">
              GPU jobs are opt-in on Premium and bill a flat ${prodJob.gpu_hour} per
              GPU-hour instead of the vCPU and memory rates — the GPU node's CPU and
              memory are included
              {prodJob.gpu_min_billable_minutes
                ? `, with a ${prodJob.gpu_min_billable_minutes}-minute minimum per run`
                : ''}. Jobs without the GPU option are unaffected.
            </p>
          )}
        </div>
      </section>

      {/* ── Tier cards ── */}
      <section className="pv2-tiers">
        {TIERS.map(tier => (
          <div key={tier.id} className={`pv2-card ${tier.featured ? 'pv2-card--featured' : ''}`}>
            {tier.badge && <div className="pv2-badge">{tier.badge}</div>}

            <div className={`pv2-card-top ${tier.accentClass}`}>
              <span className="pv2-tier-name">{tier.name}</span>
              <div className="pv2-price-row">
                {tierPrice(tier) != null ? (
                  <>
                    <span className="pv2-price-currency">$</span>
                    <span className="pv2-price-amount">{tierPrice(tier)}</span>
                  </>
                ) : (
                  <span className="pv2-price-amount">…</span>
                )}
              </div>
              <span className="pv2-price-sub">{tier.priceSub}</span>
            </div>

            <div className="pv2-card-body">
              <p className="pv2-tier-desc">{tier.desc}</p>

              <ul className="pv2-feature-list">
                {tier.features.map((f, i) => (
                  <li key={i} className="pv2-feature-item">
                    <span className="pv2-check">✓</span>
                    {f}
                  </li>
                ))}
              </ul>

              <div className="pv2-usage-block">
                <span className="pv2-usage-title">Usage charges</span>
                {(tierUsageRows(tier) || []).map((u, i) => (
                  <div key={i} className="pv2-usage-row">
                    <span className="pv2-usage-label">{u.label}</span>
                    <span className="pv2-usage-val">{u.value}</span>
                  </div>
                ))}
                {!tierUsageRows(tier) && (
                  <div className="pv2-usage-row">
                    <span className="pv2-usage-label" style={{ color: '#888' }}>Loading…</span>
                  </div>
                )}
              </div>

            </div>
          </div>
        ))}
      </section>

      {/* ── No provider account banner ── */}
      <section className="pv2-banner">
        <div className="pv2-banner-inner">
          <span className="pv2-banner-icon">☁️</span>
          <div>
            <strong>No cloud provider account needed.</strong>{' '}
            We provision and manage all AWS infrastructure on your behalf — no separate AWS bill,
            no IAM setup, no Kubernetes expertise required. Your Mermadic invoice is the only bill you pay.
          </div>
        </div>
      </section>

      {/* ── Comparison table ── */}
      <section className="pv2-compare">
        <h2 className="pv2-section-title">How we compare</h2>
        <div className="pv2-compare-wrap">
          <table className="pv2-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th className="pv2-th-us">Mermadic</th>
                <th>DIY Kubernetes</th>
                <th>AWS Batch / Lambda</th>
                <th>BYOC</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row, i) => (
                <tr key={i}>
                  <td className="pv2-td-label">{row.label}</td>
                  <td className="pv2-td-us">{row.mermadic}</td>
                  <td>{row.diy}</td>
                  <td>{row.batch}</td>
                  <td>{row.byoc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="pv2-table-footnote">* BYOC estimates based on typical platform markup rates plus AWS EKS control plane + EC2 + networking costs. Excludes cloud support plan and DevOps time.</p>
      </section>

      {/* ── Cost Scenarios ── */}
      <section className="pv2-scenarios-section">
        <div className="pv2-scenarios-inner">
          <div className="pv2-byoc-label">Cost Projection · vs BYOC</div>
          <h2 className="pv2-section-title">Cost projection at scale: Mermadic vs BYOC</h2>
          <p className="pv2-byoc-sub" style={{ color: '#4a5568', marginBottom: '2.5rem' }}>
            BYOC platforms offer a similar level of enterprise resource isolation by deploying
            dedicated infrastructure in your own cloud account. Here's how costs compare
            for equivalent dedicated workloads.
          </p>
          <div className="pv2-scenarios-grid">
            {COST_SCENARIOS.map((s, i) => (
              <div key={i} className="pv2-scenario-card">
                <div className="pv2-scenario-header">
                  <span className="pv2-scenario-label">{s.label}</span>
                  <span className="pv2-scenario-desc">{s.desc}</span>
                </div>
                <div className="pv2-scenario-cols">
                  <div className="pv2-scenario-col pv2-scenario-col--mermadic">
                    <div className="pv2-scenario-brand">Mermadic {s.mermadic.plan}</div>
                    <div className="pv2-scenario-row"><span>Base fee</span><span>{s.mermadic.base}/mo</span></div>
                    <div className="pv2-scenario-row"><span>Usage charges</span><span>{s.mermadic.usage}/mo</span></div>
                    <div className="pv2-scenario-total"><span>Total</span><span>{s.mermadic.total}/mo</span></div>
                    <div className="pv2-scenario-tag pv2-scenario-tag--green">Single bill · No cloud account</div>
                  </div>
                  <div className="pv2-scenario-col pv2-scenario-col--byoc">
                    <div className="pv2-scenario-brand">BYOC + Cloud Provider</div>
                    <div className="pv2-scenario-row"><span>Platform markup</span><span>{s.byoc.platform}</span></div>
                    <div className="pv2-scenario-row"><span>AWS cloud bill</span><span>{s.byoc.cloud}</span></div>
                    <div className="pv2-scenario-total pv2-scenario-total--byoc"><span>Total</span><span>{s.byoc.total}/mo</span></div>
                    <div className="pv2-scenario-tag pv2-scenario-tag--orange">Two bills · AWS account required</div>
                  </div>
                </div>
                <p className="pv2-scenario-note">{s.byoc.note}</p>
                {s.note && <p className="pv2-scenario-context">{s.note}</p>}
              </div>
            ))}
          </div>
          <p className="pv2-table-footnote" style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            Estimates based on typical BYOC platform markup rates plus AWS EKS control plane, EC2 on-demand instances, load balancer, and NAT gateway. Excludes cloud support plan and DevOps engineering time.
          </p>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="pv2-cta-section">
        <h2 className="pv2-cta-headline">Start with $5 on us</h2>
        <p className="pv2-cta-sub">Sign up and we'll add $5 of credit to your account — no monthly commitment, and you only pay for what you run. Spin up your cluster in under an hour.</p>
        <div className="pv2-cta-btns">
          <button className="pv2-cta-btn pv2-cta-btn--primary" onClick={() => navigate('/signup')}>
            Get Started
          </button>
        </div>
      </section>

    </div>
  );
}
